/**
 * Module-level memory of which link-list URLs have previously resolved
 * successfully via Smart Card.
 *
 * Why this exists:
 * The link-list items keep their "resolved" status in local React state and show
 * a skeleton overlay until the underlying Smart Card fires `onResolve`. That state
 * is tied to the component's mount lifetime, and the `SmartCardProvider` used by
 * the link list keeps its resolution store tied to its own mount lifetime too.
 *
 * Some consumers re-mount the link-list subtree very frequently while content
 * streams in (for example the inline Lumina link list in SAIN is re-rendered
 * on every streamed chunk, and the Rovo Extension chat panel re-mounts its tree as
 * the answer streams). Every remount would otherwise reset the local `resolved`
 * flag back to `false` and flash the loading skeleton again, producing a visible
 * resolved <-> loading flicker.
 *
 * By remembering, at module scope, that a given URL has resolved at least once, we
 * can initialise the per-item `resolved` state to `true` on a fresh mount. The
 * Smart Card still re-resolves in the background (typically synchronously from its
 * own warm store), but the user no longer sees the skeleton flash.
 *
 * This is intentionally a process-wide set keyed by URL: the same URL resolving to
 * the same content is stable, and the worst case for a stale entry is that we
 * briefly render the resolved layout for a URL that is being re-fetched — which is
 * exactly the non-flickering behaviour we want.
 *
 * Memory bound:
 * To avoid unbounded growth over a long session with many unique source URLs, the
 * set is capped at `MAX_RESOLVED_URLS` entries with simple FIFO eviction (the
 * oldest inserted URL is dropped once the cap is exceeded). `Set` preserves
 * insertion order, so the first entry is always the oldest. Evicting an old URL is
 * harmless: at worst it will show its skeleton once more the next time it appears,
 * then be re-cached.
 *
 * SSR safety:
 * In Node.js the module cache is per-process, not per-request, so a shared set
 * would otherwise leak one user's resolved URLs into another user's server render.
 * The streaming skeleton flicker this cache prevents is a purely client-side,
 * interactive concern (there is no streaming during SSR), so all reads and writes
 * are gated behind a client-only check. The check is evaluated at call time (not
 * captured once at module init) so that a process which imported this module
 * during SSR still behaves correctly once the client hydrates. On the server the
 * cache is effectively empty and `hasUrlResolved` always returns `false`, meaning
 * a server-rendered item starts in its normal unresolved state and no
 * cross-request state is shared.
 */
const isClient = (): boolean => typeof window !== 'undefined';

/**
 * Maximum number of resolved URLs to remember. Kept comfortably above the number
 * of unique sources a user is likely to see in a session, while bounding memory to
 * a small, fixed footprint (a few hundred short strings).
 */
const MAX_RESOLVED_URLS = 500;

const resolvedUrls = new Set<string>();

/**
 * Returns whether the given URL has previously resolved successfully.
 *
 * Always returns `false` on the server (see SSR safety note above).
 */
export const hasUrlResolved = (url: string | undefined): boolean =>
	isClient() && url !== undefined && resolvedUrls.has(url);

/**
 * Records that the given URL has resolved successfully.
 *
 * No-op on the server (see SSR safety note above).
 */
export const markUrlResolved = (url: string | undefined): void => {
	if (!isClient() || url === undefined || resolvedUrls.has(url)) {
		return;
	}

	// FIFO eviction: drop the oldest entry once the cap is reached so the set stays
	// bounded over long sessions. `Set` iterates in insertion order, so the first
	// value is the oldest.
	if (resolvedUrls.size >= MAX_RESOLVED_URLS) {
		const oldest = resolvedUrls.values().next().value;
		if (oldest !== undefined) {
			resolvedUrls.delete(oldest);
		}
	}

	resolvedUrls.add(url);
};

/**
 * Internal reset used only to clear the module-level cache.
 *
 * Do NOT call this from production code: flushing the cache mid-session would
 * reintroduce the loading skeleton flicker this cache prevents. It is intentionally
 * named with an `__unsafe` prefix and is only meant to be consumed via
 * `resolved-url-cache.test-utils.ts` (see `clearResolvedUrlCache` there).
 */
export const __unsafeResetResolvedUrlCache = (): void => {
	resolvedUrls.clear();
};
